import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardHeader, CardContent } from '@/components/Card'
import { priceForPlan } from '@/lib/priceConversions'
import useCartStore from '@/store/cart'
import useRegionStore from '@/store/region'
import { useToast } from '@/components/ui/use-toast'
import Product from '@/types/Product'
import { MotionButton } from '@/components/ui/motion-button'
import { regionalSKUs } from '@/data/regional_skus'
import { loadRegionData } from '@/lib/services/regions'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MapPin } from 'lucide-react'

type Region = { value: string; label: string, status: string }

function Regional() {
  const { toast } = useToast()
  const { addItemToCart, cartItems } = useCartStore()
  const { confirmedRegion, setConfirmedRegion } = useRegionStore()

  const [isDialogOpen, setIsDialogOpen] = useState(
    () => !useRegionStore.getState().confirmedRegion
  )
  const [selectedRegion, setSelectedRegion] = useState<string>(
    () => useRegionStore.getState().confirmedRegion
  )
  const { data: regions = [], isLoading: isLoadingRegions } = useQuery<Region[]>({
    queryKey: ['regions'],
    queryFn: loadRegionData,
    enabled: isDialogOpen,
    staleTime: 0,
  })

  const selectedRegionData = regions.find((region) => region.value === selectedRegion)
  const isComingSoon = selectedRegionData?.status === 'coming_soon'

  const handleAddToCartClick = (plan: Product) => {
    if (confirmedRegion) {
      enroll(plan)
      return
    }
    setIsDialogOpen(true)
  }
  const handleCancel = () => {
    setIsDialogOpen(false)
    setSelectedRegion('')
  }
  const handleConfirmRegion = () => {
    if (isComingSoon) {
      window.open(
        'https://cti.formstack.com/forms/cti_regional_training_form',
        '_blank',
        'noopener,noreferrer'
      )
      return
    }
    if (!selectedRegion) return
    setConfirmedRegion(selectedRegion)
    setIsDialogOpen(false)
    toast({
      title: 'Region Confirmed',
      description: 'You can now add your subscriptions to the cart.',
    })
  }

  const renderUSD = (price) =>
  price.split('USD').map((part, index, arr) => (
    <React.Fragment key={index}>
      {part}
      {index < arr.length - 1 && (
        <span className="text-sm mx-1">USD</span>
      )}
    </React.Fragment>
  ))

  const enroll = (plan: Product) => {
    if (!confirmedRegion) {
      toast({
        title: 'Region Required',
        description: 'Please select a training region',
        variant: 'destructive',
      })
      return
    }

    try {
      if (cartItems.some((item) => item.id === plan.id)) {
        toast({
          title: 'Already in Cart',
          description: 'You already have the selected seats',
        })
        return
      }

      const result = addItemToCart(plan)
      if (result.success) {
        toast({
          title: 'Added Plan to Cart',
          description: `Added plan: ${plan.id} to your cart`,
        })
      } else {
        toast({
          title: result.errors.base || 'You already have the selected seats',
          variant: 'destructive',
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add item to cart',
        variant: 'destructive',
      })
    }
  }

  return (
    <div>
      <div className="w-full lg:w-3/4 m-auto text-center py-8">
        <div className="flex flex-wrap justify-center gap-6 py-4">
          {regionalSKUs.plans.map((course, index) => {
            const mappedCourse: Product = {
              ...course,
              product_type:
                course.product_type === 'ilt'
                  ? 'ilt'
                  : course.product_type === 'olt'
                  ? 'olt'
                  : 'vilt',
            }
            return (
              <div key={`spc_${index}`} className="w-full md:w-[30%] min-w-[320px] flex">
                <Card className="flex flex-col w-full">
                  <CardHeader>{course.name}</CardHeader>
                  {course.class_hours != null ? (
                    <p className="text-xs text-primary mb-2 font-medium text-center">
                      Single Seat | {course.class_hours}-Hour Class
                    </p>
                  ) : (
                    <p className="text-xs text-primary mb-2 font-medium text-center">
                      up to {course.seats} Seats
                    </p>
                  )}

                  <CardContent>
                    <div className="flex flex-col justify-between h-full">
                      <div>
                        <hr className="text-primary mt-4" />
                        {course.class_hours != null ? (
                          <ul className="text-sm text-grays-dark mb-4 font-normal mt-4 list-disc text-left px-6 ">
                            <li className="mb-2">
                              {course.description}
                            </li>
                          </ul>
                        ) : (
                          <ul className="text-sm text-grays-dark mb-4 font-normal mt-4 list-disc text-left px-6 ">
                            <li className="mb-2">
                              Send up to {course.seats} technicians to all CTI
                              Regional Training classes in your area.
                            </li>
                            <li className="mb-2">Nine nights of training per year.</li>
                            <li className="mb-2">
                              Can send different techs to each class.
                            </li>
                          </ul>
                        )}
                      </div>
                      <div className="mt-auto">
                        <div className="w-auto px-4 mx-auto mb-4">
                          {course.class_hours != null ? (
                            <p className="text-md text-primary font-semibold my-2 text-center">
                              {renderUSD(priceForPlan(mappedCourse))}{' '}
                              <span className="text-sm font-medium text-grays-dark">
                                (One Time Purchase)
                              </span>
                            </p>
                          ) : (
                            <p className="text-md text-primary font-semibold my-2 text-center">
                              {renderUSD(priceForPlan(mappedCourse))}{' '}
                              <span className="text-sm font-medium text-grays-dark">
                                (Annual Commitment)
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <MotionButton
                        onClick={() => handleAddToCartClick(mappedCourse)}
                      >
                        Add to Cart
                      </MotionButton>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )
          })}
        </div>
      </div>

      <Dialog open={isDialogOpen}>
        <DialogOverlay className="bg-blue-900/40" />
        <DialogContent
          className="sm:max-w-[600px] border-blue-100"
          hideCloseButton
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-center text-xl">
              Regional Training
            </DialogTitle>

            <DialogDescription className="text-center text-base text-black leading-relaxed">
              In-person Instructor Led Training is currently available in a
              limited number of locations. Please select your location from the
              dropdown list.
            </DialogDescription>

          </DialogHeader>

          <div className="py-2 space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Training Regions
            </label>
            <Select
              value={selectedRegion}
              onValueChange={setSelectedRegion}
              disabled={isLoadingRegions}
            >
              <SelectTrigger className="w-[22rem] h-11 text-base focus:ring-0 focus:ring-offset-0">
                <SelectValue
                  placeholder={
                    isLoadingRegions ? 'Loading regions...' : 'Select your region'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem
                    key={region.value}
                    value={region.value}
                    className="text-base py-3 h-8 group focus:ring-transparent"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground group-hover:text-white" />
                      {region.label} {region.status === "coming_soon" && <span className="text-base group-hover:text-white">(Coming Soon!)</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogDescription className="text-center text-base text-black leading-relaxed">
              Don't see your region listed? Fill out{" "}
              <a
                href="https://cti.formstack.com/forms/cti_regional_training_survey"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 font-semibold underline hover:text-blue-800"
              >
                this survey
              </a>{" "}
              to register your interest in having in-person training in your area.
            </DialogDescription>

          <DialogFooter className="gap-2 sm:gap-0 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1 sm:flex-none text-white bg-secondary hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleConfirmRegion}
              disabled={!selectedRegion}
              className="flex-1 sm:flex-none gap-2 text-white bg-primary hover:text-white"
            >
              {isComingSoon ? <span>Register</span> : <span>Proceed</span>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Regional
